import { Component, OnInit } from '@angular/core';
import {
  FinBanrisulApiService,
  BanrisulWebhookLogItem,
  SalvarBanrisulApiConfigPayload
} from 'src/app/service/fin-banrisul-api.service';
import {
  BANRISUL_EMAIL_HOMOLOGACAO,
  BANRISUL_HOMOLOGACAO,
  BANRISUL_SANDBOX_BENEFICIARIOS_TESTE,
  BANRISUL_URLS,
} from 'src/app/constants/banrisul-financeiro.constants';
import { UtilService } from 'src/app/service/util.service';

@Component({
  selector: 'app-configuracao-api-banrisul',
  templateUrl: './configuracao-api-banrisul.component.html',
  styleUrls: ['./configuracao-api-banrisul.component.scss']
})
export class ConfiguracaoApiBanrisulComponent implements OnInit {
  carregando = false;
  salvandoConfig = false;
  testandoConexao = false;
  testandoWebhook = false;
  testandoConsultaLiquidado = false;
  clientSecretConfigurado = false;
  codigoBeneficiarioCompleto = '';
  webhookUrlSugerida = '';
  webhookLogs: BanrisulWebhookLogItem[] = [];
  apiClientId = '';
  apiClientSecret = '';
  emailHomologacao = BANRISUL_EMAIL_HOMOLOGACAO;
  beneficiariosSandbox = BANRISUL_SANDBOX_BENEFICIARIOS_TESTE;
  urlsHomologacao = BANRISUL_URLS.homologacao;
  urlsCertificacao = BANRISUL_URLS.certificacao;
  urlsProducao = BANRISUL_URLS.producao;
  /** Texto exibido no HTML — chaves literais não podem ficar no template Angular 9. */
  consultaBoletoApiPath = 'GET /boletos/{id}';

  configForm: SalvarBanrisulApiConfigPayload = {
    descricao: 'Banrisul',
    agencia: '',
    digitoAgencia: '',
    contaCorrente: '',
    digitoConta: '',
    nomeBeneficiarioCobranca: '',
    apiClientId: '',
    apiClientSecret: '',
    apiAmbiente: 'H',
    codigoBeneficiario: '',
    codigoBeneficiarioCompleto: '',
    webhookUrl: '',
    webhookBoletoAtivo: true,
    apiMultaPercentual: null,
    apiJurosPercentualMensal: null,
    apiMultaDiasAposVencimento: 0,
  };

  constructor(
    private banrisulApi: FinBanrisulApiService,
    private util: UtilService
  ) {}

  ngOnInit(): void {
    this.carregarConfig();
  }

  carregarConfig(): void {
    this.carregando = true;
    this.util.showLoading();
    this.banrisulApi.obterConfig().subscribe({
      next: (cfg) => {
        this.util.closeLoading();
        this.carregando = false;
        this.clientSecretConfigurado = cfg.clientSecretConfigurado;
        this.codigoBeneficiarioCompleto = cfg.codigoBeneficiarioCompleto;
        this.webhookUrlSugerida = cfg.webhookUrlSugerida || '';
        if (cfg.configurado) {
          this.configForm.descricao = cfg.descricao || this.configForm.descricao || 'Banrisul';
          this.configForm.agencia = cfg.agencia || this.configForm.agencia;
          this.configForm.digitoAgencia = cfg.digitoAgencia || this.configForm.digitoAgencia;
          this.configForm.contaCorrente = cfg.contaCorrente || this.configForm.contaCorrente;
          this.configForm.digitoConta = cfg.digitoConta || this.configForm.digitoConta;
          this.configForm.nomeBeneficiarioCobranca =
            cfg.nomeBeneficiarioCobranca || this.configForm.nomeBeneficiarioCobranca;
          this.configForm.apiAmbiente =
            cfg.apiAmbiente === 'P' ? 'P' : cfg.apiAmbiente === 'C' ? 'C' : 'H';
          this.configForm.codigoBeneficiario = cfg.codigoBeneficiario || this.configForm.codigoBeneficiario;
          this.configForm.codigoBeneficiarioCompleto =
            cfg.codigoBeneficiarioCompleto || this.configForm.codigoBeneficiarioCompleto;
          if (cfg.apiClientId) {
            this.apiClientId = cfg.apiClientId;
          }
          this.configForm.webhookUrl = cfg.webhookUrl || cfg.webhookUrlSugerida || this.configForm.webhookUrl;
          this.configForm.webhookBoletoAtivo = cfg.webhookBoletoAtivo;
          this.configForm.apiMultaPercentual = cfg.apiMultaPercentual ?? null;
          this.configForm.apiJurosPercentualMensal = cfg.apiJurosPercentualMensal ?? null;
          this.configForm.apiMultaDiasAposVencimento = cfg.apiMultaDiasAposVencimento ?? 0;
          this.carregarWebhookLogs();
        } else if (this.ehHomologacao()) {
          this.aplicarDadosHomologacao(false);
        }
      },
      error: () => {
        this.util.closeLoading();
        this.carregando = false;
        this.util.toasterShowError('Erro', 'Não foi possível carregar a configuração.');
      }
    });
  }

  onAmbienteChange(): void {
    if (this.ehHomologacao()) {
      this.aplicarDadosHomologacao(true);
    } else if (this.ehCertificacao()) {
      this.util.toasterShowInfo(
        'Certificação',
        'Use Client ID/Secret de produção, beneficiário real e registre boletos com ambiente T até retorno 04.'
      );
    }
  }

  ehHomologacao(): boolean {
    return this.configForm.apiAmbiente === 'H';
  }

  ehCertificacao(): boolean {
    return this.configForm.apiAmbiente === 'C';
  }

  ehProducao(): boolean {
    return this.configForm.apiAmbiente === 'P';
  }

  usaHostProducao(): boolean {
    return this.ehCertificacao() || this.ehProducao();
  }

  /** Preenche conta/beneficiário conforme seção Ambiente Sandbox da documentação Banrisul. */
  aplicarDadosHomologacao(notificar = true): void {
    const h = BANRISUL_HOMOLOGACAO;
    this.configForm.descricao = h.descricao;
    this.configForm.agencia = h.agencia;
    this.configForm.digitoAgencia = '';
    this.configForm.contaCorrente = '';
    this.configForm.digitoConta = h.digitoConta;
    this.configForm.nomeBeneficiarioCobranca = h.nomeBeneficiarioCobranca;
    this.configForm.codigoBeneficiario = h.codigoBeneficiario;
    this.configForm.codigoBeneficiarioCompleto = h.codigoBeneficiarioCompleto;
    this.codigoBeneficiarioCompleto = h.codigoBeneficiarioCompleto;
    if (notificar) {
      this.util.toasterShowInfo(
        'Homologação (Sandbox)',
        `Dados de teste aplicados (beneficiário ${h.codigoBeneficiarioCompleto}). ` +
          'Informe Client ID e Secret do portal e salve antes de testar a conexão.'
      );
    }
  }

  salvarConfiguracao(): void {
    const clientId = String(this.apiClientId || this.configForm.apiClientId || '').trim();
    const clientSecret = String(this.apiClientSecret || this.configForm.apiClientSecret || '').trim();

    if (!clientId) {
      this.util.toasterShowWarning('Configuração', 'Client ID é obrigatório.');
      return;
    }
    if (!this.clientSecretConfigurado && !clientSecret) {
      this.util.toasterShowWarning('Configuração', 'Client Secret é obrigatório na primeira configuração.');
      return;
    }
    if (this.configForm.apiAmbiente === 'P' && !clientSecret) {
      this.util.toasterShowWarning(
        'Configuração',
        'Em produção informe o Client Secret de produção (não reutiliza o do Sandbox).'
      );
      return;
    }
    if (this.configForm.apiAmbiente === 'C' && !clientSecret && !this.clientSecretConfigurado) {
      this.util.toasterShowWarning(
        'Configuração',
        'Em certificação informe o Client Secret de produção.'
      );
      return;
    }
    if (
      !String(this.configForm.codigoBeneficiario || '').trim() &&
      !String(this.configForm.codigoBeneficiarioCompleto || '').trim()
    ) {
      this.util.toasterShowWarning('Configuração', 'Informe o código do beneficiário (7 dígitos) ou o código completo (13 dígitos).');
      return;
    }

    const payload: SalvarBanrisulApiConfigPayload = {
      ...this.configForm,
      apiAmbiente:
        this.configForm.apiAmbiente === 'P' ? 'P' : this.configForm.apiAmbiente === 'C' ? 'C' : 'H',
      apiClientId: clientId,
      apiClientSecret: clientSecret,
    };

    this.salvandoConfig = true;
    this.util.showLoading();
    this.banrisulApi.salvarConfig(payload).subscribe({
      next: (response: any) => {
        this.util.closeLoading();
        this.salvandoConfig = false;
        if (response?.hasErrors) {
          this.util.toasterShowError('Configuração', response.message || 'Não foi possível salvar.');
          return;
        }
        this.util.toasterShowSuccess('Configuração', 'Dados salvos com sucesso.');
        this.apiClientSecret = '';
        this.carregarConfig();
      },
      error: () => {
        this.util.closeLoading();
        this.salvandoConfig = false;
        this.util.toasterShowError('Erro', 'Falha ao salvar configuração.');
      }
    });
  }

  testarConexao(): void {
    const clientId = String(this.apiClientId || this.configForm.apiClientId || '').trim();
    const clientSecret = String(this.apiClientSecret || this.configForm.apiClientSecret || '').trim();
    const apiAmbiente =
      this.configForm.apiAmbiente === 'P' ? 'P' : this.configForm.apiAmbiente === 'C' ? 'C' : 'H';

    if (!clientId) {
      this.util.toasterShowWarning('Conexão', 'Informe o Client ID antes de testar.');
      return;
    }
    if (this.usaHostProducao() && !clientSecret && !this.clientSecretConfigurado) {
      this.util.toasterShowWarning(
        'Conexão',
        'Em certificação/produção informe o Client Secret de produção no campo e salve, ou cole-o agora para testar.'
      );
      return;
    }

    this.testandoConexao = true;
    this.util.showLoading();
    this.banrisulApi.testarConexao({
      apiClientId: clientId,
      apiClientSecret: clientSecret || undefined,
      apiAmbiente,
    }).subscribe({
      next: (response: any) => {
        this.util.closeLoading();
        this.testandoConexao = false;
        if (response?.hasErrors) {
          this.util.toasterShowError('Conexão', response.message || 'Falha ao testar conexão.');
          return;
        }
        const msg = response?.data?.mensagem || response?.message || 'Conexão OK.';
        this.util.toasterShowSuccess('Conexão', msg);
      },
      error: (err: any) => {
        this.util.closeLoading();
        this.testandoConexao = false;
        this.util.toasterShowError('Conexão', err?.error?.message || err?.message || 'Falha ao testar conexão.');
      }
    });
  }

  usarUrlWebhookSugerida(): void {
    if (this.webhookUrlSugerida) {
      this.configForm.webhookUrl = this.webhookUrlSugerida;
    }
  }

  carregarWebhookLogs(): void {
    this.banrisulApi.listarWebhookLogs(20).subscribe({
      next: (logs) => {
        this.webhookLogs = logs;
      }
    });
  }

  testarWebhook(): void {
    const url = String(this.configForm.webhookUrl || this.webhookUrlSugerida || '').trim();
    if (!url) {
      this.util.toasterShowWarning('Webhook', 'Informe a URL HTTPS do webhook e salve a configuração.');
      return;
    }
    this.testandoWebhook = true;
    this.util.showLoading();
    this.banrisulApi.testarWebhook(url).subscribe({
      next: (response: any) => {
        this.util.closeLoading();
        this.testandoWebhook = false;
        if (response?.hasErrors) {
          this.util.toasterShowError('Webhook', response.message || 'Falha ao testar webhook.');
          return;
        }
        const msg = response?.data?.mensagem || response?.message || 'Teste enviado.';
        this.util.toasterShowSuccess('Webhook', msg);
        setTimeout(() => this.carregarWebhookLogs(), 2000);
      },
      error: (err: any) => {
        this.util.closeLoading();
        this.testandoWebhook = false;
        this.util.toasterShowError('Webhook', err?.error?.message || err?.message || 'Falha ao testar webhook.');
      }
    });
  }

  testarConsultaLiquidado(): void {
    this.testandoConsultaLiquidado = true;
    this.util.showLoading();
    this.banrisulApi.consultarExemploLiquidado().subscribe({
      next: (response: any) => {
        this.util.closeLoading();
        this.testandoConsultaLiquidado = false;
        if (response?.hasErrors) {
          this.util.toasterShowError('Consulta', response.message || 'Falha na consulta de exemplo.');
          return;
        }
        const d = response?.data || response;
        const msg = d?.mensagem || d?.status || 'Consulta concluída.';
        this.util.toasterShowSuccess('Consulta sandbox', msg);
      },
      error: (err: any) => {
        this.util.closeLoading();
        this.testandoConsultaLiquidado = false;
        this.util.toasterShowError('Consulta', err?.error?.message || err?.message || 'Falha na consulta.');
      }
    });
  }
}
